/**
 * Notification Inbox Component
 *
 * Dropdown panel showing user's watch zone notifications.
 * Supports infinite scroll, mark as read, and dismiss actions.
 */

import { useEffect, useRef, useCallback, type ReactNode } from 'react';
import { useWatchZones } from '../contexts/WatchZoneContext';
import { WatchZoneNotification } from '../api/client';
import {
  FlameIcon,
  AlertTriangleIcon,
  ClipboardListIcon,
  CheckCircleIcon,
  BellIcon,
} from './icons';
import './NotificationInbox.css';

interface NotificationInboxProps {
  onClose: () => void;
  onViewAll?: () => void;
  onEventSelect?: (eventId: string) => void;
}

// Format relative time (e.g., "2 hours ago")
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Get severity color class
function getSeverityClass(severity?: string): string {
  switch (severity) {
    case 'EMERGENCY':
      return 'severity-emergency';
    case 'WATCH_AND_ACT':
      return 'severity-watch';
    case 'ADVICE':
      return 'severity-advice';
    default:
      return '';
  }
}

// Get notification icon based on alert type. SVG components, not emoji,
// for cross-WebView consistency (Samsung/Pixel/OnePlus emoji fonts differ).
function getNotificationIcon(alertType: string): ReactNode {
  switch (alertType) {
    case 'NEW_INCIDENT':
      return <FlameIcon size={20} />;
    case 'ESCALATION':
      return <AlertTriangleIcon size={20} />;
    case 'UPDATE':
      return <ClipboardListIcon size={20} />;
    case 'RESOLVED':
      return <CheckCircleIcon size={20} />;
    default:
      return <BellIcon size={20} />;
  }
}

export default function NotificationInbox({ onViewAll, onEventSelect }: NotificationInboxProps) {
  const {
    notifications,
    unreadCount,
    isLoadingNotifications,
    totalNotifications,
    refreshNotifications,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
    dismiss,
  } = useWatchZones();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Load notifications on mount
  useEffect(() => {
    refreshNotifications();
  }, [refreshNotifications]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoadingNotifications) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 100;

    if (nearBottom && notifications.length < totalNotifications) {
      loadMoreNotifications();
    }
  }, [isLoadingNotifications, notifications.length, totalNotifications, loadMoreNotifications]);

  // Handle notification click
  const handleNotificationClick = async (notification: WatchZoneNotification) => {
    if (notification.status === 'UNREAD') {
      await markAsRead(notification.notificationId);
    }
    // Navigate to the event on the map
    if (onEventSelect && notification.eventId) {
      onEventSelect(notification.eventId);
    }
  };

  // Handle dismiss
  const handleDismiss = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    await dismiss(notificationId);
  };

  return (
    <div className="notification-inbox" role="dialog" aria-label="Notifications">
      {/* Header */}
      <div className="notification-inbox-header">
        <h3 id="notification-inbox-title">Notifications</h3>
        {unreadCount > 0 && (
          <button
            className="notification-inbox-mark-all"
            onClick={markAllAsRead}
            aria-label="Mark all notifications as read"
          >
            Mark all read
          </button>
        )}
      </div>

      {/* Notification List */}
      <div
        className="notification-inbox-list"
        ref={scrollContainerRef}
        onScroll={handleScroll}
        role="list"
        aria-labelledby="notification-inbox-title"
      >
        {notifications.length === 0 && !isLoadingNotifications ? (
          <div className="notification-inbox-empty">
            <span className="notification-inbox-empty-icon">🔔</span>
            <p>No notifications yet</p>
            <span className="notification-inbox-empty-hint">
              You'll be notified when incidents occur in your watch zones
            </span>
          </div>
        ) : (
          <>
            {notifications.map((notification) => (
              <div
                key={notification.notificationId}
                className={`notification-inbox-item ${notification.status === 'UNREAD' ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
                role="listitem"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleNotificationClick(notification);
                  }
                }}
                aria-label={`${notification.status === 'UNREAD' ? 'Unread: ' : ''}${notification.title}. ${notification.severity}. ${notification.zoneName}. ${formatRelativeTime(notification.createdAt)}`}
              >
                <div className="notification-inbox-item-icon">
                  {getNotificationIcon(notification.alertType)}
                </div>

                <div className="notification-inbox-item-content">
                  <div className="notification-inbox-item-header">
                    <span className={`notification-inbox-item-severity ${getSeverityClass(notification.severity)}`}>
                      {notification.severity?.replace('_', ' ') || notification.alertType.replace('_', ' ')}
                    </span>
                    <span className="notification-inbox-item-time">
                      {formatRelativeTime(notification.createdAt)}
                    </span>
                  </div>

                  <p className="notification-inbox-item-title">
                    {notification.title}
                  </p>

                  <p className="notification-inbox-item-body">
                    {notification.body}
                  </p>

                  <p className="notification-inbox-item-zone">
                    {notification.zoneName} • {notification.distanceKm.toFixed(1)} km away
                  </p>
                </div>

                <button
                  className="notification-inbox-item-dismiss"
                  onClick={(e) => handleDismiss(e, notification.notificationId)}
                  title="Dismiss notification"
                  aria-label={`Dismiss "${notification.title}" notification`}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))}

            {isLoadingNotifications && (
              <div className="notification-inbox-loading">
                <span className="loading-spinner small"></span>
                Loading...
              </div>
            )}

            {!isLoadingNotifications && notifications.length < totalNotifications && (
              <button
                className="notification-inbox-load-more"
                onClick={loadMoreNotifications}
              >
                Load more
              </button>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="notification-inbox-footer">
        <span className="notification-inbox-count">
          {totalNotifications} notification{totalNotifications !== 1 ? 's' : ''}
        </span>
        {onViewAll && (
          <button className="notification-inbox-view-all" onClick={onViewAll}>
            View all
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
